import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useLoading } from '@/providers/LoadingProvider';
import { useCreateBusinessPartner } from '@/hooks/api/0450_create_business_partner';
import { useInvalidateBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useBusinessPartnerContexts } from '@/hooks/api/0440_business_partner_contexts';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Ref, useEffect, useRef, useState } from 'react';
import { toastApiError, toastError, toastSuccess } from '@/lib/toast';
import { BusinessPartner } from '@/hooks/api/models';
import { Link } from 'react-router-dom';
import { useFindCorporateName } from '@/hooks/api/2270_find_houjin_name';
import { BUSINESS_PARTNER_DISPLAY_TYPES } from '@/lib/constants';

type FormInputs = {
  identifier: string;
  name: string;
  display_name: string;
  description: string;
  business_partner_context_id: number;
  corporate_number: string;
  display_type: 'both' | 'sales' | 'purchase';
};

type Props = {
  onCreated: (businessPartner: BusinessPartner) => void;
  submitButtonRef: Ref<HTMLButtonElement>;
  displayType?: 'both' | 'sales' | 'purchase';
};

export const BusinessPartnerCreateForm = ({ onCreated, submitButtonRef, displayType = 'both' }: Props) => {
  const { _w_ } = useCurrentWorkspace();
  const { setShowLoading } = useLoading();
  const { mutate, isPending, data, error } = useCreateBusinessPartner();
  const { invalidate } = useInvalidateBusinessPartners(_w_!.identifier);
  const { data: businessPartnerContexts } = useBusinessPartnerContexts();
  const [findCorporateNameParams, setFindCorporateNameParams] = useState({
    identifier: _w_!.identifier,
    corporate_number: '',
    enabled: false,
  });
  const {
    data: findCorporateNameResp,
    isLoading: findCorporateNameRespIsLoading,
    error: findCorporateNameError,
    refetch,
  } = useFindCorporateName(findCorporateNameParams);
  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      business_partner_context_id: 1,
      display_type: displayType,
    },
  });
  const [detailOpened, setDetailOpened] = useState(false);
  const openDetailLabel = useRef<HTMLLabelElement>(null);

  useEffect(
    () => setShowLoading(isPending || findCorporateNameRespIsLoading),
    [isPending, findCorporateNameRespIsLoading]
  );
  useEffect(() => toastApiError(findCorporateNameError), [findCorporateNameError]);

  useEffect(() => {
    if (data) {
      toastSuccess('取引先を登録しました');
      onCreated(data);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    if (data.display_name == '') {
      data.display_name = data.name;
    }
    mutate(data);
  };

  const onCorporateNumberClick = () => {
    setFindCorporateNameParams({ ...findCorporateNameParams, corporate_number: getValues('corporate_number') });
  };

  useEffect(() => {
    if (findCorporateNameParams.corporate_number == null || findCorporateNameParams.corporate_number.length == 0) {
      return;
    }

    if (findCorporateNameParams.corporate_number.length == 13) {
      refetch();
    } else {
      toastError('法人番号は半角数字13桁で入力してください。');
    }
  }, [findCorporateNameParams]);

  useEffect(() => {
    if (findCorporateNameResp) {
      setValue('name', findCorporateNameResp.name);
      setValue('business_partner_context_id', findCorporateNameResp.business_partner_context.id);
      if (!detailOpened) {
        openDetailLabel.current?.click();
      }
    }
  }, [findCorporateNameResp]);

  return (
    <form>
      <ApiErrorMessages error={error} />

      <div className="form-group">
        <label>法人番号</label>
        <div className="d-flex align-items-center">
          <input
            type="text"
            maxLength={13}
            className="form-control flex-1"
            placeholder={'法人番号'}
            {...register('corporate_number', {})}
          />
          <button type="button" className="btn btn-icon bg-gradient-secondary ms-1" onClick={onCorporateNumberClick}>
            法人番号から取得
          </button>
        </div>
        <div className="text-xs text-muted mt-1">半角数字13桁で入力してください。</div>
      </div>
      <input type="checkbox" className="opener-checkbox" id="click-to-open-opener" />
      <label
        htmlFor="click-to-open-opener"
        className="click-to-open open-hide text-decoration-underline"
        ref={openDetailLabel}
        onClick={() => setDetailOpened(true)}
      >
        個人などで法人番号がない取引先の登録はこちら
      </label>

      <div className="click-to-open-target">
        <div className="form-group">
          <label>
            正式名称<span className="text-danger">*</span>
          </label>
          <input
            type="text"
            maxLength={50}
            className={`form-control ${errors.name && 'is-invalid'}`}
            placeholder={'正式名称'}
            {...register('name', {
              required: '入力して下さい。',
            })}
          />
        </div>
        <div className="form-group">
          <label>表示名</label>
          <input
            type="text"
            maxLength={50}
            className={`form-control ${errors.display_name && 'is-invalid'}`}
            placeholder={'表示名'}
            {...register('display_name')}
          />
        </div>
        <div className="form-group">
          <label>メモ</label>
          <textarea
            rows={5}
            className={`form-control ${errors.description && 'is-invalid'}`}
            {...register('description')}
          ></textarea>
        </div>
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label>
              事業者種別<span className="text-danger">*</span>
            </label>
            <select className="form-control" required={true} {...register('business_partner_context_id')}>
              {businessPartnerContexts &&
                businessPartnerContexts.map((c) => (
                  <option key={`context-${c.id}`} value={c.id}>
                    {c.label}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label>
              表示設定<span className="text-danger">*</span>
            </label>
            <select className="form-control" required={true} {...register('display_type')}>
              {['both', 'sales', 'purchase'].map((t) => (
                <option key={`display-type-${t}`} value={t}>
                  {BUSINESS_PARTNER_DISPLAY_TYPES[t]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="btn bg-gradient-dark btn-icon"
        style={{ display: 'none' }}
        onClick={() => handleSubmit(onSubmit)()}
        ref={submitButtonRef}
      >
        <i className="fas fa-check-circle"></i>新規登録する
      </button>
    </form>
  );
};
